import { AutoComplete, Button, Spin, Table, Select, Modal, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faMagnifyingGlass,
} from "@fortawesome/pro-solid-svg-icons";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "../../helpers/dayjsSetup";
import PurchaseOrderService from "../../services/PurchaseOrder/PurchaseOrderService";
import { useGlobalFilters } from "../../providers/GlobalFiltersProvider";
import { useQuery } from "@tanstack/react-query";
import WarehouseFilter from "../../components/Filters/WarehouseFilter";
import { purchaseOrderStatus } from "../../common/enums/purchaseOrderStatus";
import useDebounced from "../../customHooks/useDebounced";

import { Card, Tabs } from "antd";

const PurchaseOrderGatewayPage = ({ warehouses }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filters } = useGlobalFilters();
  const [activeTab, setActiveTab] = useState(location?.state?.activeTab || "submitted");
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [searchInput, setSearchInput] = useState(
    location?.state?.purchaseOrderNumber
      ? `${location?.state?.purchaseOrderNumber} - ${location?.state?.activeTab.replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())}`
      : "",
  );
  const debouncedSearch = useDebounced(searchInput, 500);
  const [list, setList] = useState({
    request: {
      purchaseOrderNumber: location?.state?.purchaseOrderNumber || null,
      pageFilter: { all: false, size: 50, num: 0 },
      sortFilter: { field: "poDate", order: "desc" },
      status: purchaseOrderStatus.Submitted,
    },
  });
  
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState(location.state?.purchaseOrderId ?? null);

  const { purchaseOrderNumber, pageFilter, sortFilter } = list.request;

  const { data: counts = {} } = useQuery({
    queryKey: ["purchaseOrderListTabCounts", purchaseOrderNumber, pageFilter.num, pageFilter.size, sortFilter.field, sortFilter.order, filters],
    queryFn: async () => PurchaseOrderService.getPurchaseOrderCounts({
      ...list.request, ...filters,
      status: getPurchaseOrderStatus(activeTab),
    }),
  });

  const { data: purchaseOrderList, isFetching: loading } = useQuery({
    queryKey: ["purchaseOrderList", purchaseOrderNumber, activeTab, pageFilter.num, pageFilter.size, sortFilter.field, sortFilter.order, filters],
    queryFn: async () => PurchaseOrderService.getPurchaseOrders({
      ...list.request, ...filters,
      status: getPurchaseOrderStatus(activeTab),
    }),
    staleTime: 5 * 60 * 1000,
  });

  const tabs = ["submitted", "awaitingConfirmation", "supplierConfirmed", "pendingShipment", "shipmentRequested", "shipmentBooked", "shipmentConfirmed"];

  const getPurchaseOrderStatus = (value) => {
    if (!value) return undefined;

    const statusMap = {
      submitted: purchaseOrderStatus.Submitted,
      awaitingConfirmation: purchaseOrderStatus.AwaitingConfirmation,
      supplierConfirmed: purchaseOrderStatus.SupplierConfirmed,
      pendingShipment: purchaseOrderStatus.PendingShipment,
      shipmentRequested: purchaseOrderStatus.ShipmentRequested,
      shipmentBooked: purchaseOrderStatus.ShipmentBooked,
      shipmentConfirmed: purchaseOrderStatus.ShipmentConfirmed,
    };

    return statusMap[value];
  };

  useEffect(() => {
    const defaultSorts = {
      submitted: { field: "poDate", order: "desc" },
      awaitingConfirmation: { field: "poDate", order: "desc" },
      supplierConfirmed: { field: "poDate", order: "desc" },
      pendingShipment: { field: "poDate", order: "desc" },
      shipmentRequested: { field: "poDate", order: "desc" },
      shipmentBooked: { field: "poDate", order: "desc" },
      shipmentConfirmed: { field: "poDate", order: "desc" },
    };

    setList((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        pageFilter: { ...prev.request.pageFilter, num: 0 },
        sortFilter: defaultSorts[activeTab] || { field: "", order: "" },
        status: getPurchaseOrderStatus(activeTab),
      },
    }));
  }, [activeTab]);

  const leftAlign = { align: "left" };
  const rightAlign = { align: "right" };
  const centerAlign = { align: "center" };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD MMM YYYY") : "-";

  const dateColumn = (
    title,
    dataIndex,
    classNames = "w-28",
    defaultSortOrder = "",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...leftAlign,
    render: (value) => <div className={classNames}>{formatDate(value)}</div>,
    sorter: (a, b) => new Date(a[dataIndex]) - new Date(b[dataIndex]),
    defaultSortOrder,
  });

  const leftColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...leftAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),
    sorter: sortable
      ? (a, b) => {
        const valA = a[dataIndex];
        const valB = b[dataIndex];

        if (!isNaN(valA) && !isNaN(valB)) {
          return Number(valA) - Number(valB);
        }

        return (valA || "").toString().localeCompare((valB || "").toString());
      }
      : false,
  });

  const centerColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28",
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...centerAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),

    sorter: sortable
      ? (a, b) => {
        const valA = a[dataIndex];
        const valB = b[dataIndex];

        // Handle numbers first
        if (!isNaN(valA) && !isNaN(valB)) {
          return Number(valA) - Number(valB);
        }

        // Fallback to string comparison
        return (valA || "").toString().localeCompare((valB || "").toString());
      }
      : false,
  });

  const handleView = (record) => {
    navigate("/purchase-order/details", {
      state: {
        pathname: location.pathname,
        purchaseOrderId: record.id,
        activeTab,
        purchaseOrderNumber: list.request.purchaseOrderNumber,
      },
    });
  };

  const rawSearch = debouncedSearch?.split(" - ")[0]?.trim();

  const { data: searchResults = [], isFetching: searchLoading } = useQuery({
    queryKey: ["poSearch", rawSearch, filters],
    queryFn: () => PurchaseOrderService.searchPurchaseOrdersWithFilters({ purchaseOrderNumber: rawSearch, ...filters }),
    enabled: !!rawSearch,
    staleTime: 5 * 60 * 1000,
  });

  const handleSearchInputChange = (value) => {
    setSearchInput(value);
  };

  const handlePOSelect = (selectedPO) => {
    setSearchInput("");
    const { tabName, purchaseOrderNumber } = selectedPO;
    setIsSearchModalVisible(false);
    setActiveTab(tabName);
    setList((prev) => ({
      ...prev,
      request: { ...prev.request, purchaseOrderNumber },
    }));
  };

  const handleCancelModal = () => {
    setSearchInput("");
    setIsSearchModalVisible(false);
  };

  const handleClear = () => {
    setSearchInput("");
  };

  const handleClearSearchResult = () => {
    setSearchInput("");
    setList((prev) => ({
      ...prev,
      request: { ...prev.request, purchaseOrderNumber: null },
    }));
  };

  const currentField = list.request.sortFilter.field;
  const currentOrder =
    list.request.sortFilter.order === "asc"
      ? "ascend"
      : list.request.sortFilter.order === "desc"
        ? "descend"
        : null;

  const columns = [
    {
      ...leftColumn(
        <div className="ml-12">PO Number</div>,
        "poNumber",
        (_, record) => (
          <div className="ml-8">
            <Button
              className={`w-40 overflow-hidden whitespace-nowrap font-bold text-sm ${selectedPurchaseOrderId === record.id ? `bg-sky-400` : ``}`}
              type="primary"
              onClick={() => handleView(record)}
            >
              {record.poNumber}
            </Button>
          </div>
        ),
        true,
        "w-40", // Explicit width to match Button
      ),
      sortOrder: currentField === "poNumber" ? currentOrder : null,
    },
    {
      ...leftColumn(
        "Supplier",
        "supplierName",
        (_, record) => (
          <div className="ml-8 mr-8">
            <Button
              type="primary"
              className="w-28 overflow-hidden whitespace-nowrap font-bold text-sm bg-[#789B9E]"
              onClick={() =>
                navigate("/settings/supplier/details", {
                  state: {
                    mode: "edit",
                    supplier: record.supplierDetails,
                  },
                })
              }
            >
              {record.supplierName}
            </Button>
          </div>
        ),
        true,
        "w-20",
      ),
      sortOrder: currentField === "supplierName" ? currentOrder : null,
    },
    {
      ...dateColumn(
        <div>
          PO Date
        </div>, "poDate", "w-36"),
      sortOrder: currentField === "poDate" ? currentOrder : null,
    },
    {
      ...leftColumn(
        "Contents",
        "contentSummary",
        (value) => <div className="w-24">{value}</div>,
        true,
        "w-24",
      ),
      sortOrder: currentField === "contentSummary" ? currentOrder : null,
    },
    {
      ...leftColumn(
        "Status",
        "statusName",
        (value) => <div className="w-24">{value}</div>,
        true,
        "w-24",
      ),
      sortOrder: currentField === "statusName" ? currentOrder : null,
    },
    {
      ...dateColumn(
        <div>
          Status Date
        </div>, "statusDate", "w-36"),
      sortOrder: currentField === "statusDate" ? currentOrder : null,
    },
    leftColumn(
      <div>Is Urgent</div>,
      "isUrgent",
      (_, record) => (
        <div className="flex items-center justify-center w-8 mr-8">
          <span
            className={`w-4 h-4 opacity-60 rounded-full`}
            style={{
              backgroundColor: handleGetUrgencyColorClass(
                record.isUrgent,
              ),
            }}
          />
        </div>
      ),
      false,
      "w-8",
    ),
  ];

  const columnsMap = {
    submitted: columns,
    awaitingConfirmation: columns,
    supplierConfirmed: columns,
    pendingShipment: columns,
    shipmentRequested: columns,
    shipmentBooked: columns,
    shipmentConfirmed: columns,
  };

  const handleTableChange = (pagination = {}, filters, sorter) => {
    setList((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        sortFilter: {
          field: sorter.field || "",
          order: sorter.order === "ascend" ? "asc" : (sorter.order === "descend" ? "desc" : ""),
        },
        pageFilter: {
          ...prev.request.pageFilter,
          size: pagination.pageSize || prev.request.pageFilter.size,
          num: (pagination.current ?? prev.request.pageFilter.num + 1) - 1,
        },
      },
    }));
  };

  const handleGetUrgencyColorClass = (isUrgent) => {
    if (isUrgent) return "#EF4444";
    return "#22C55E";
  };

  const renderTable = (viewKey) => {
    const columns = columnsMap[viewKey];
    const current = (list.request?.pageFilter?.num ?? 0) + 1;
    const pageSize = list.request?.pageFilter?.size ?? 50;
    const total = purchaseOrderList?.totalCount ?? 0;
    const totalPages = Math.ceil(total / pageSize);

    const currentSorter = {
      field: list.request.sortFilter.field,
      order:
        list.request.sortFilter.order === "asc"
          ? "ascend"
          : list.request.sortFilter.order === "desc"
            ? "descend"
            : "",
    };

    const onCustomPageChange = (page, size) => {
      handleTableChange({ current: page, pageSize: size }, {}, currentSorter); // Pass current sorter instead of {}.
    };

    const onPageSizeChange = (size) => {
      onCustomPageChange(1, size); // reset to first page when size changes
    };
    return (
      <div className="">
        <Spin spinning={loading}>
          <Table
            showSorterTooltip={false}
            columns={columns}
            dataSource={
              loading
                ? new Array(12).fill(null).map((_, index) => ({ key: index }))
                : purchaseOrderList?.data?.map((item, index) => ({ ...item, key: index }))
            }
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
            size="small"
            sticky
            className="min-h-[calc(100vh - 300px)] h-[calc(100vh - 300px)]"
            rowClassName={(_, index) =>
              index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
            }
          />
          <div className="flex justify-between items-center mt-2">
            {/* Rows per page selector + info */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <Select
                value={String(pageSize || 50)}
                onChange={(val) => onPageSizeChange(Number(val))}
                size="medium"
                style={{ width: 80 }}
                options={[
                  { value: '10', label: '10' },
                  { value: '20', label: '20' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' },
                ]}
             />
            </div>

            {/* Custom buttons */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {`${(current - 1) * pageSize + 1}-${Math.min(
                  current * pageSize,
                  total,
                )} of ${total} items`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(1, pageSize)}
                  disabled={current === 1}
                >
                  <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(current - 1, pageSize)}
                  disabled={current === 1}
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(current + 1, pageSize)}
                  disabled={current === totalPages}
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(totalPages, pageSize)}
                  disabled={current === totalPages}
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                </Button>
              </div>
            </div>
          </div>
        </Spin>
      </div>
    );
  };

  return (
    <div className="px-2 relative  w-full h-full">
      <div className="flex  flex-col md:flex-row justify-between items-center w-full my-2 px-2">
        <div className="flex justify-start items-center font-inter">
          <span
            className="!font-medium !text-[#015373] text-[30px] !m-0 pl-4 mt-10"
          >
            Purchase Order Gateway
          </span>
        </div>
        <div className="flex  flex-col md:flex-row justify-between font-inter gap-1">
          <AutoComplete
            className="self-end border w-72 !h-[34px] mr-4"
            placeholder="Search POs"
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="opacity-70 mr-2"
              />
            }
            options={searchResults.map((item) => ({
              value: item.purchaseOrderNumber, // what shows in input when selected
              label: (
                <div className="p-2">
                  <span
                    className="font-bold">{item.purchaseOrderNumber}</span>&nbsp;-&nbsp;{item.tabName.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                </div>
              ),
              item,
            }))}
            value={searchInput}
            onChange={handleSearchInputChange}
            onSelect={(value, option) => {
              const formattedTabName = option.item.tabName
                .replace(/([A-Z])/g, " $1") // split camelCase
                .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter

              handlePOSelect(option.item);

              // Input will show plain text (no bold possible)
              setSearchInput(`${option.item.purchaseOrderNumber} - ${formattedTabName}`);
            }}
            allowClear
            onClear={handleClearSearchResult}
            notFoundContent={
              searchLoading ? <Spin size="small" /> : "No results found"
            }
          />
          <WarehouseFilter warehouses={warehouses} />

          <Modal
            title="Search POs"
            open={isSearchModalVisible}
            onCancel={handleCancelModal}
            footer={null}
            destroyOnClose
          >
            <Input
              placeholder="Enter PO number"
              value={searchInput}
              onChange={handleSearchInputChange}
              onClear={handleClear}
              allowClear
            />
            <Spin spinning={searchLoading}>
              <div className="mt-2 max-h-96 overflow-auto">
                {searchResults.length > 0 ? (
                  searchResults.map((item, index) => (
                    <div
                      key={index}
                      className="p-2 hover:bg-gray-200 cursor-pointer border-b"
                      onClick={() => handlePOSelect(item)}
                    >
                      <div>
                        <span className="font-bold">{item.purchaseOrderNumber}</span> -{" "}
                        {item.tabName
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 mt-4 text-center">
                    No results found
                  </div>
                )}
              </div>
            </Spin>
          </Modal>
        </div>
      </div>
      <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
        <Tabs
          type="card"
          tabBarGutter={8}
          activeKey={activeTab}
          onChange={(activeKey) => setActiveTab(activeKey)}
          items={tabs.map((key) => ({
            key,
            label: (
              <>
                <span className="text-[#015373] font-medium">
                  {key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </span>
                <span className="px-2 ml-2 rounded-md bg-[#01A4A6] font-semibold text-white">
                  {counts[key] ?? 0}
                </span>
              </>
            ),
            children: renderTable(key),
          }))}
        />
      </Card>
    </div>
  );
};

export default PurchaseOrderGatewayPage;